import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Alert
} from '@mui/material';
import {
    Security as SecurityIcon,
    Draw as DrawIcon,
    Language as LanguageIcon
} from '@mui/icons-material';
import SignatureModal from '../jha/SignatureModal';

export default function TBTAcknowledgmentModal({
    open,
    onClose,
    employee,
    tbtTopic,
    existingSignature,
    onSign
}) {
    const [acknowledged, setAcknowledged] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [language, setLanguage] = useState('english');
    const [scrolledLanguages, setScrolledLanguages] = useState(new Set());
    const contentRef = React.useRef(null);

    // Reset acknowledged state when modal opens/closes
    React.useEffect(() => {
        if (open && existingSignature) {
            setAcknowledged(true);
            setScrolledLanguages(new Set(['english', 'spanish']));
        } else if (!open) {
            setAcknowledged(false);
            setScrolledLanguages(new Set());
        }
    }, [open, existingSignature]);

    // Check if current language is scrolled
    const hasScrolledToBottom = scrolledLanguages.has(language);

    // Handle scroll to check if user scrolled to bottom
    const handleScroll = (e) => {
        const element = e.target;
        const isAtBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 10;
        if (isAtBottom && !scrolledLanguages.has(language)) {
            setScrolledLanguages(prev => new Set([...prev, language]));
        }
    };

    if (!employee || !tbtTopic) return null;

    const handleAcknowledgeChange = (event) => {
        const isChecked = event.target.checked;
        setAcknowledged(isChecked);

        if (isChecked) {
            // Automatically open signature modal
            setSignatureModalOpen(true);
        }
    };

    const handleSignClick = () => {
        if (!acknowledged) return;
        setSignatureModalOpen(true);
    };

    const handleSignatureSave = (signatureData) => {
        onSign(signatureData);
        setSignatureModalOpen(false);
        setAcknowledged(false);
        onClose();
    };

    const handleRemoveSignature = () => {
        // Remove signature by passing null
        onSign(null);
    };

    const instructionText = language === 'english' ? tbtTopic.textEnglish : tbtTopic.textSpanish;

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                disablePortal={false}
            >
                <DialogTitle sx={{ py: 2, px: 3, backgroundColor: '#f8f9fa' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <SecurityIcon sx={{ fontSize: 28, color: '#1976d2' }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" fontWeight={600}>Tool Box Talk</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {employee.name || employee.FullName}
                            </Typography>
                        </Box>
                        <ToggleButtonGroup
                            value={language}
                            exclusive
                            onChange={(e, newLang) => {
                                if (newLang) {
                                    setLanguage(newLang);
                                }
                            }}
                            size="small"
                        >
                            <ToggleButton value="english">
                                English
                            </ToggleButton>
                            <ToggleButton value="spanish">
                                Spanish
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </DialogTitle>
                <DialogContent
                    ref={contentRef}
                    onScroll={handleScroll}
                    sx={{ px: 3, py: 2, maxHeight: '60vh', overflow: 'auto' }}
                >
                    <Typography variant="h6" gutterBottom fontWeight={600} color="primary">
                        {tbtTopic.title}
                    </Typography>

                    {instructionText ? (
                        <Box
                            sx={{
                                p: 2,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                backgroundColor: '#f9f9f9',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'inherit'
                            }}
                        >
                            <Typography variant="body1">
                                {instructionText}
                            </Typography>
                        </Box>
                    ) : (
                        <Alert severity="info">
                            No instruction text available in {language === 'english' ? 'English' : 'Spanish'}
                        </Alert>
                    )}
                </DialogContent>

                <Divider />

                <Box sx={{ px: 3, py: 2, backgroundColor: '#f8f9fa' }}>
                    {!hasScrolledToBottom && !existingSignature ? (
                        <Alert severity="info">
                            Please scroll to the bottom to continue
                        </Alert>
                    ) : (
                        <>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={acknowledged}
                                        onChange={handleAcknowledgeChange}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body1" fontWeight={500}>
                                        I have read and understood this Tool Box Talk
                                    </Typography>
                                }
                            />
                            {existingSignature && (
                                <Box sx={{ mt: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: 'white' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                        Current Signature:
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            maxWidth: 200,
                                            height: 60,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <img src={existingSignature} alt="signature" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </Box>

                <Divider />

                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button onClick={onClose}>
                        Close
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    {existingSignature && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleRemoveSignature}
                            size="small"
                        >
                            Remove Signature
                        </Button>
                    )}
                    {acknowledged && (
                        <Button
                            variant="contained"
                            startIcon={<DrawIcon />}
                            onClick={handleSignClick}
                        >
                            {existingSignature ? 'Change Signature' : 'Sign'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {signatureModalOpen && createPortal(
                <SignatureModal
                    allowAuto
                    open={signatureModalOpen}
                    onClose={() => setSignatureModalOpen(false)}
                    existingSignature={existingSignature || ''}
                    employeeName={employee.name || employee.FullName}
                    onSave={handleSignatureSave}
                />,
                document.body
            )}
        </>
    );
}
